/** The literal token the `@Player AI` suggestion inserts. */
export const PLAYER_AI_MENTION_TOKEN = "@Player AI";

/** Who a mention selected from the picker addresses. */
export type MentionTarget =
  | { readonly kind: "player_ai" }
  | {
      readonly kind: "person";
      readonly userId: string;
      /** The literal `@Name` inserted into the draft. */
      readonly token: string;
    };

/** A picker selection: the exact characters it inserted, and who they name. */
export interface SelectedMention {
  readonly start: number;
  readonly end: number;
  readonly target: MentionTarget;
}

/**
 * Keep picker selections attached to the characters they inserted while the
 * draft is edited around them. An edit that touches a selected token turns
 * that token back into ordinary text; every other selection shifts with the
 * text before it.
 */
export function reconcileSelectedMentionRanges<
  T extends { readonly start: number; readonly end: number },
>(previous: string, next: string, ranges: readonly T[]): T[] {
  if (previous === next) return [...ranges];

  let editStart = 0;
  while (
    editStart < previous.length &&
    editStart < next.length &&
    previous[editStart] === next[editStart]
  ) {
    editStart += 1;
  }

  let unchangedSuffix = 0;
  while (
    unchangedSuffix < previous.length - editStart &&
    unchangedSuffix < next.length - editStart &&
    previous[previous.length - unchangedSuffix - 1] ===
      next[next.length - unchangedSuffix - 1]
  ) {
    unchangedSuffix += 1;
  }

  const previousEditEnd = previous.length - unchangedSuffix;
  const nextEditEnd = next.length - unchangedSuffix;
  const delta = nextEditEnd - previousEditEnd;

  return ranges.flatMap((range) => {
    if (previousEditEnd <= range.start) {
      return [{ ...range, start: range.start + delta, end: range.end + delta }];
    }
    if (editStart >= range.end) return [range];
    // An edit overlapping a selected occurrence turns that occurrence back
    // into ordinary text. Other selected occurrences remain structured.
    return [];
  });
}

/**
 * Zero-based ordinals, among ALL occurrences of `token` in `text`, of the
 * occurrences that were selected from the picker. A typed or pasted lookalike
 * is an occurrence too — it simply is not selected — which is what lets the
 * server tell the two apart without trusting the text.
 */
export function mentionOccurrenceIndices(
  text: string,
  token: string,
  ranges: ReadonlyArray<{ readonly start: number; readonly end: number }>,
): number[] {
  if (!token) return [];
  const selected = new Set(
    ranges.map((range) => `${range.start}:${range.end}`),
  );
  const result: number[] = [];
  let ordinal = 0;
  let start = text.indexOf(token);
  while (start >= 0) {
    const end = start + token.length;
    if (selected.has(`${start}:${end}`)) result.push(ordinal);
    ordinal += 1;
    start = text.indexOf(token, end);
  }
  return result;
}

/**
 * The people a sent draft mentions, one entry per person with the ordinals of
 * their selected token. Two selections of the same person are one entry.
 */
export function selectedPersonMentions(
  text: string,
  mentions: readonly SelectedMention[],
): Array<{ userId: string; token: string; occurrenceIndices: number[] }> {
  const byPerson = new Map<
    string,
    { token: string; ranges: Array<{ start: number; end: number }> }
  >();
  for (const mention of mentions) {
    if (mention.target.kind !== "person") continue;
    const entry = byPerson.get(mention.target.userId) ?? {
      token: mention.target.token,
      ranges: [],
    };
    entry.ranges.push({ start: mention.start, end: mention.end });
    byPerson.set(mention.target.userId, entry);
  }
  return [...byPerson.entries()].flatMap(([userId, entry]) => {
    const occurrenceIndices = mentionOccurrenceIndices(
      text,
      entry.token,
      entry.ranges,
    );
    return occurrenceIndices.length > 0
      ? [{ userId, token: entry.token, occurrenceIndices }]
      : [];
  });
}

/** Does a picker candidate match what was typed after `@`? */
export function mentionCandidateMatches(label: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = label.toLowerCase();
  return (
    haystack.startsWith(needle) ||
    haystack.split(/\s+/).some((word) => word.startsWith(needle))
  );
}
